<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_requests', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.status')" for="status" />
                        <FormSelect
                            required
                            id="gradable"
                            v-model="form.status"
                            :options="options.status"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.status" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-8">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <EmptyState picture v-if="!gamificationRequests.data.length">
            <template v-slot:picture />
        </EmptyState>
        <div class="flex flex-col mt-5" v-else>
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.gamification_requests') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.status') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.message') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.when') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(gamificationRequest, index) in gamificationRequests.data"
                                :key="gamificationRequest.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex space-x-2 items-center">
                                        <img :src="gamificationRequest.image_path" alt="" class="h-6" />
                                        <div>
                                            <div>
                                                {{ gamificationRequest.requested_name }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="mt-1">
                                        <BadgePill color="green" v-if="gamificationRequest.status === 'granted'">
                                            {{ $t('messages.request_granted') }}
                                        </BadgePill>
                                        <BadgePill color="yellow" v-else-if="gamificationRequest.status === 'rejected'">
                                            {{ $t('messages.request_denied') }}
                                        </BadgePill>
                                        <BadgePill color="blue" v-else-if="gamificationRequest.status === 'pending'">
                                            {{ $t('messages.request_pending') }}
                                        </BadgePill>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-pre-line max-w-sm text-sm leading-5 text-gray-500">
                                    {{ gamificationRequest.message }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div :class="{ 'text-gray-300': gamificationRequest.is_updated }">
                                        {{
                                            $t('messages.requested_ago', {
                                                ago: gamificationRequest.created_at_diff_for_humans,
                                            })
                                        }}
                                    </div>
                                    <div v-if="gamificationRequest.is_updated">
                                        {{
                                            $t('messages.updated_ago', {
                                                ago: gamificationRequest.updated_at_diff_for_humans,
                                            })
                                        }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="gamificationRequests" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import BadgePill from '@/components/BadgePill.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        BadgePill,
        FormError,
        FormSelect,
        FormLabel,
        PanelPage,
        EmptyState,
        InertiaPagination,
    },
    layout: Layout,
    name: 'Show',
    props: ['filters', 'meta', 'options', 'group', 'gamificationRequests'],
    data() {
        return {
            form: this.$inertia.form({
                status: this.filters.status,
            }),
            submitForm: this.$inertia.form({
                gamification_requests_ids: [],
                status: 'confirm',
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        submit() {
            this.submitForm.patch(this.meta.links.update, {
                preserveScroll: true,
                onSuccess: () => {
                    this.submitForm.reset();
                },
            });
        },
        submitConfirm() {
            this.submitForm.status = 'confirm';
            this.submit();
        },
        submitReturn() {
            this.submitForm.status = 'reject';
            this.submit();
        },
        selectAll() {
            this.submitForm.gamification_requests_ids = this.gamificationRequests.data.map((i) => i.id);
        },
    },
    computed: {
        isShowingRequests: function () {
            return this.filters.status === 'requested';
        },
    },
};
</script>
